import React from 'react';
import { StyleSheet } from 'react-native';

import { Accordion } from '@/components/Accordion';
import { AnimatedMicroMarketDescription } from '@/components/AnimatedMicroMarketDescription';
import { SgpIcon } from '@/components/SgpIcon';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { Event, MarketCategory } from '@/feature/event-details-sbk//types';
import { MarketName } from '@/feature/event-details-sbk/components/MarketName';
import { MarketLayouts } from '@/feature/event-details-sbk/components/market-layouts';
import { usePublishedMarkets } from '@/feature/event-details-sbk/hooks/use-market-cache';
import { useIsSgpEnabled } from '@/hooks/use-is-sgp-enabled';

type MarketAccordionProps = {
    marketCategory: MarketCategory;
    event: Event;
    isExpanded?: boolean;
    testID?: string;
    appendAlt?: boolean;
};

export const MarketAccordion = ({
    marketCategory,
    event,
    isExpanded,
    testID,
    appendAlt = false,
}: MarketAccordionProps) => {
    const publishedMarkets = usePublishedMarkets(marketCategory.markets);
    const isSgpEnabled = useIsSgpEnabled(event.is_sgp_enabled);

    if (!publishedMarkets.length) {
        return null;
    }

    return (
        <Accordion
            header={
                <Box justifyContent="space-between" flexDirection="row" alignItems="center">
                    <Box flexShrink={1}>
                        <MarketName
                            sportName={event.sport.name}
                            event={event}
                            fallback={marketCategory.markets[0]?.description}
                            marketType={marketCategory.markets[0]?.market_type}
                            testID={`marketName-${testID}`}
                            color={ds.colorToken.text.primary}
                            numberOfLines={1}
                            fontSize={15}
                            appendAlt={appendAlt}
                        />
                        <AnimatedMicroMarketDescription
                            event={event}
                            marketType={marketCategory.markets[0]?.market_type}
                            showDescription={true}
                        />
                    </Box>
                    {isSgpEnabled ? (
                        <Box mr="s12" ml="s12">
                            <SgpIcon />
                        </Box>
                    ) : null}
                </Box>
            }
            content={
                <MarketLayouts
                    {...{ event, markets: publishedMarkets, layout: marketCategory.layout, testID: testID }}
                />
            }
            initialExpanded={isExpanded}
            accordionStyle={styles.accordionStyle}
            contentStyle={styles.contentStyle}
        />
    );
};

export const styles = StyleSheet.create({
    accordionStyle: {
        borderBottomColor: ds.colorValue.border.strong,
        borderBottomWidth: 1,
    },
    contentStyle: {
        paddingBottom: 24,
        paddingTop: 4,
    },
});
